/**
 * useOutreachSSE — invalidates the Outreach tab's queries on every SSE event
 * for this campaign. Every payload here is a "go refetch" nudge, not enough to
 * patch the cache directly (e.g. outreach_contacts_found only carries
 * entry_ids, not the found contact value) — so every event just invalidates.
 *
 * Mounted directly from OutreachTab, not from useCampaignDetailSSE — keeps a
 * brand-side page from ever subscribing to this admin-only topic, even by
 * accident. outreach_import_progress/outreach_import_done are ALSO consumed
 * by useImportWizard's own outreach-mode progress tracking (Task 27) for its
 * per-batch query — two independent useSSE subscribers to the same event
 * name is exactly how sseManager's shared pub/sub is meant to be used.
 *
 * 2.3 adds the message-status trio and the mailbox pause (BE
 * internal/outreach/events/sse.go):
 *   • outreach_message_sent | _failed | _held carry {campaign_id, entry_id,
 *     message_id, status}. The terminal kind rides payload.status, not the
 *     event name, so all three are handled identically here.
 *   • The thread key is invalidated too: nothing else ever touches it (neither
 *     the draft/approve hooks nor the row invalidation does), and useOutreachThread
 *     sets no staleTime, so an open ThreadDrawer would otherwise show the
 *     pre-send state until it remounted.
 *   • outreach_mailbox_paused, outreach_mailbox_resync and
 *     outreach_pool_unhealthy carry NO campaign_id — the pool is global and
 *     these events go to the admin tier — so none of the three are filtered
 *     the way the campaign-scoped events are (onUnmatched treats a missing
 *     campaign_id the same way, for the same reason).
 *
 * 2.5 adds three more (ruling 68, BE internal/outreach/events/sse.go):
 *   • outreach_draft_ready{campaign_id, entry_id, message_id, step} — a
 *     follow-up landed in the approval queue; refreshes the drafts list, the
 *     counts and the one row.
 *   • outreach_rate_agreed{campaign_id, entry_id} and
 *     outreach_entry_updated{campaign_id, entry_id, change} both mean "this
 *     entry's status/deliverables/pills changed" — same invalidation set as
 *     a message event, plus the row and the KPIs (a status change moves the
 *     funnel/by-stage numbers).
 *
 * outreach_entry_projected{campaign_id, entry_id, contact_changed} fires once
 * the fold has written the entry, so it takes the same set. It is the only
 * signal that outreach.contact moved (e.g. to not_found): the refetch that
 * outreach_contacts_found triggers can land before the fold does.
 *
 * CRM v2: every mailbox event also refreshes the CRM's mailbox-health card,
 * and every entry change (rate agreed included) refreshes its Rates &
 * agreements list.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OutreachKpis } from '@discovr/types';
import { toast } from 'sonner';
import { useSSE, sseManager } from '@discovr/common';
import { campaignKeys } from '../queryKeys';
import { kpisRefreshIntervalMs } from './useOutreachKpis';
import { useThrottledInvalidate } from './useThrottledInvalidate';


interface OutreachEventPayload {
  campaign_id?: string;
}

interface OutreachMessageEventPayload extends OutreachEventPayload {
  entry_id?: string;
  message_id?: number;
  status?: string;
  held_reason?: string;
}

interface OutreachMailboxPausedPayload {
  mailbox_id?: number;
  address?: string;
  reason?: string;
}

/** Shared by outreach_pool_unhealthy ({reason}) and outreach_mailbox_resync
 *  ({mailbox_id, address, phase}) — the two payloads don't overlap, so every
 *  field here is optional and onPoolEvent branches on which ones are present. */
interface OutreachPoolPayload {
  reason?: string;
  mailbox_id?: number;
  address?: string;
  phase?: 'started' | 'finished' | 'slow';
}

interface OutreachDraftReadyPayload extends OutreachEventPayload {
  entry_id?: string;
  message_id?: number;
  step?: number;
}

interface OutreachEntryChangedPayload extends OutreachEventPayload {
  entry_id?: string;
  change?: string;
  /** outreach_entry_projected only; deliberately not a gate (see the header). */
  contact_changed?: boolean;
}

export function useOutreachSSE(campaignId: string): void {
  const queryClient = useQueryClient();
  const enabled = !!campaignId;

  // Safety net for a stream drop (Wi-Fi blip, deploy, a second tab displacing
  // this connection) whose gap outruns the BE's Last-Event-ID replay ring
  // buffer: without this, a reply/bounce/unmatched-message emitted during the
  // gap is simply never seen until a manual refresh. Independent of
  // useCampaignSSE's own roster-scoped reconnect handler — sseManager
  // supports multiple.
  useEffect(() => {
    if (!enabled) return;
    return sseManager.addReconnectHandler(() => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'outreach'] });
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachStatus() });
      queryClient.invalidateQueries({ queryKey: ['outreach', 'thread'] });
    });
  }, [campaignId, enabled, queryClient]);

  const kpisKey = useMemo(() => campaignKeys.outreachKpis(campaignId), [campaignId]);
  // The KPI read is the heaviest outreach query: a busy campaign's events refresh it no more often than Go's cache
  // would answer with a new view, which the last view says itself.
  const kpisInterval = useCallback(
    () => kpisRefreshIntervalMs(queryClient.getQueryData<OutreachKpis>(kpisKey)),
    [queryClient, kpisKey],
  );
  const invalidateKpis = useThrottledInvalidate(kpisKey, kpisInterval);

  const invalidateRows = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  }, [campaignId, queryClient]);

  const onEvent = useCallback(
    (payload: OutreachEventPayload) => {
      if (payload.campaign_id !== campaignId) return;
      invalidateRows();
    },
    [campaignId, invalidateRows],
  );

  const onMessageEvent = useCallback(
    (payload: OutreachMessageEventPayload) => {
      if (payload.campaign_id !== campaignId) return;
      invalidateRows();
      if (payload.entry_id) {
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachThread(payload.entry_id) });
      }
    },
    [campaignId, invalidateRows, queryClient],
  );

  const invalidateMailboxes = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachPool(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachStatus() });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachMailboxHealth(campaignId) });
  }, [campaignId, queryClient]);

  const onMailboxPaused = useCallback(
    (payload: OutreachMailboxPausedPayload) => {
      invalidateMailboxes();
      const address = payload.address || 'A pool mailbox';
      toast.warning(payload.reason ? `${address} was paused — ${payload.reason}` : `${address} was paused`);
    },
    [invalidateMailboxes],
  );

  const onUnmatched = useCallback((payload: OutreachEventPayload) => {
    if (payload.campaign_id && payload.campaign_id !== campaignId) return;
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachStatus() });
    toast.info('New unmatched reply');
  }, [campaignId, queryClient]);

  const onPoolEvent = useCallback((payload: OutreachPoolPayload) => {
    invalidateMailboxes();
    if (payload.reason) {
      toast.warning(`Outreach pool needs attention: ${payload.reason}`);
    } else if (payload.phase) {
      const address = payload.address || 'A pool mailbox';
      if (payload.phase === 'finished') toast.success(`${address} resync finished`);
      else if (payload.phase === 'slow') toast.warning(`${address} resync is taking a while`);
      else toast.info(`${address} resync started`);
    }
  }, [invalidateMailboxes]);

  const onDraftReady = useCallback(
    (payload: OutreachDraftReadyPayload) => {
      if (payload.campaign_id !== campaignId) return;
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachBulkSends(campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
      if (payload.entry_id) {
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRow(campaignId, payload.entry_id) });
      }
    },
    [campaignId, queryClient],
  );

  const onEntryChanged = useCallback(
    (payload: OutreachEntryChangedPayload) => {
      if (payload.campaign_id !== campaignId) return;
      invalidateRows();
      invalidateKpis();
      queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRatesAll(campaignId) });
      if (payload.entry_id) {
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRow(campaignId, payload.entry_id) });
        queryClient.invalidateQueries({ queryKey: campaignKeys.outreachThread(payload.entry_id) });
      }
    },
    [campaignId, invalidateRows, invalidateKpis, queryClient],
  );

  useSSE('outreach_import_progress', enabled, onEvent);
  useSSE('outreach_import_done', enabled, onEvent);
  useSSE('outreach_operation_progress', enabled, onEvent);
  useSSE('outreach_operation_done', enabled, onEvent);
  useSSE('outreach_contacts_found', enabled, onEvent);
  useSSE('outreach_message_sent', enabled, onMessageEvent);
  useSSE('outreach_message_failed', enabled, onMessageEvent);
  useSSE('outreach_message_held', enabled, onMessageEvent);
  useSSE('outreach_mailbox_paused', enabled, onMailboxPaused);
  useSSE('outreach_reply_received', enabled, onMessageEvent);
  useSSE('outreach_reply_parsed', enabled, onMessageEvent);
  useSSE('outreach_bounce', enabled, onMessageEvent);
  useSSE('outreach_unmatched_message', enabled, onUnmatched);
  useSSE('outreach_mailbox_resync', enabled, onPoolEvent);
  useSSE('outreach_pool_unhealthy', enabled, onPoolEvent);
  useSSE('outreach_draft_ready', enabled, onDraftReady);
  useSSE('outreach_rate_agreed', enabled, onEntryChanged);
  useSSE('outreach_entry_updated', enabled, onEntryChanged);
  useSSE('outreach_entry_projected', enabled, onEntryChanged);
}
